import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/utils/api';
import { useAuthStore } from '@/store';
import type { LoginCredentials, RegisterCredentials } from '@/types';

export function useLogin() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  
  return useMutation({
    mutationFn: (credentials: LoginCredentials) =>
      authApi.login(credentials.username, credentials.password),
    onSuccess: (data) => {
      setAuth(data.user, data.access_token);
      navigate('/');
    },
  });
}

export function useRegister() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  
  return useMutation({
    mutationFn: (credentials: RegisterCredentials) =>
      authApi.register(credentials.email, credentials.username, credentials.password),
    onSuccess: (data) => {
      setAuth(data.user, data.access_token);
      navigate('/');
    },
  });
}

export function useDebugLogin() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  
  return useMutation({
    mutationFn: async () => {
      console.log('[useDebugLogin] mutationFn called');
      const result = await authApi.debugLogin();
      console.log('[useDebugLogin] mutationFn result:', result);
      return result;
    },
    onSuccess: (data) => {
      console.log('[useDebugLogin] onSuccess, token:', data.access_token.substring(0, 20) + '...');
      // Use user from response or create default
      const user = data.user || { username: 'debug_test', email: 'debug_test@example.com' };
      setAuth(user, data.access_token);
      // Small delay to ensure store is updated before navigation
      setTimeout(() => {
        console.log('[useDebugLogin] Navigating to /');
        navigate('/');
      }, 100);
    },
    onError: (error: any) => {
      console.error('[useDebugLogin] onError:', error);
      console.error('[useDebugLogin] Error response:', error.response);
    },
  });
}

export function useLogout() {
  const navigate = useNavigate();
  const { clearAuth } = useAuthStore();
  const queryClient = useQueryClient();
  
  return () => {
    clearAuth();
    queryClient.clear();
    navigate('/login');
  };
}
